import { useState, useCallback } from 'react';
import { Task, Comment, Tag, CommentsProgress } from '../types';
import { fetchTasksFromAsana, fetchParentTasksWithSubtasks, fetchMentionsFromAsana, fetchPendingComments } from '../utils/apiUtils';
import { getTaskTags, setTaskTags } from '../utils/taskUtils';

export const useDashboardData = (accessToken: string, userId: string, workspaceMembershipId: string) => {
  const [assignedTasks, setAssignedTasks] = useState<Task[]>([]);
  const [parentTasksWithSubtasks, setParentTasksWithSubtasks] = useState<Task[]>([]);
  const [mentions, setMentions] = useState<Task[]>([]);
  const [pendingComments, setPendingComments] = useState<Comment[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsSearchCompleted, setCommentsSearchCompleted] = useState(false);
  const [commentsProgress, setCommentsProgress] = useState<CommentsProgress>({
    current: 0,
    total: 0,
    message: '',
    currentProject: '',
    currentTask: '',
    tasksInProject: 0,
    currentTaskIndex: 0
  });

  const extractAndStoreExistingTags = useCallback((tasksData: Task[]) => {
    const existingTags = new Set<string>();
    
    tasksData.forEach(task => {
      const taskTags = getTaskTags(task.gid);
      taskTags.forEach(tag => existingTags.add(tag));
    });
    
    // Use functional state update to avoid dependency on tags
    setTags(prevTags => {
      const newTags: Tag[] = [];
      const existingTagNames = new Set(prevTags.map(tag => tag.name));
      
      existingTags.forEach(tagName => {
        if (!existingTagNames.has(tagName)) {
          newTags.push({
            id: `existing-${tagName}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            name: tagName
          });
        }
      });
      
      return newTags.length > 0 ? [...prevTags, ...newTags] : prevTags;
    });
  }, []); // Remove tags dependency to prevent infinite loop

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [assignedTasksData, parentTasksData, mentionsData] = await Promise.all([
        fetchTasksFromAsana(accessToken, userId),
        fetchParentTasksWithSubtasks(accessToken, userId),
        fetchMentionsFromAsana(accessToken, userId),
      ]);
      
      setAssignedTasks(assignedTasksData);
      setParentTasksWithSubtasks(parentTasksData);
      setMentions(mentionsData);
      
      // Extract and store all existing tags from tasks
      const allTasks = [...assignedTasksData, ...parentTasksData];
      extractAndStoreExistingTags(allTasks);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [accessToken, userId, extractAndStoreExistingTags]);

  const fetchCommentsData = useCallback(async () => {
    try {
      setCommentsLoading(true);
      setCommentsSearchCompleted(false);
      setCommentsProgress({ 
        current: 0, 
        total: 0, 
        message: 'Starting search...',
        currentProject: '',
        currentTask: '',
        tasksInProject: 0,
        currentTaskIndex: 0
      });
      
      const pendingCommentsData = await fetchPendingComments(accessToken, workspaceMembershipId, userId, setCommentsProgress);
      setPendingComments(pendingCommentsData);
      setCommentsSearchCompleted(true);
    } catch (error) {
      console.error('Error fetching comments data:', error);
    } finally {
      setCommentsLoading(false);
      setCommentsProgress({ 
        current: 0, 
        total: 0, 
        message: '',
        currentProject: '',
        currentTask: '',
        tasksInProject: 0,
        currentTaskIndex: 0
      });
    }
  }, [accessToken, workspaceMembershipId, userId]);

  const refreshData = async () => {
    setRefreshing(true);
    setCommentsSearchCompleted(false);
    try {
      await fetchData();
    } finally {
      setRefreshing(false);
    }
  };

  const addTagToTask = useCallback((taskId: string, tagName: string) => {
    const currentTags = getTaskTags(taskId);
    if (!currentTags.includes(tagName)) {
      const updatedTags = [...currentTags, tagName];
      setTaskTags(taskId, updatedTags);
      
      // Update the task in state using functional updates to avoid stale closures
      setAssignedTasks(prevTasks => prevTasks.map(task => 
        task.gid === taskId 
          ? { ...task, tags: updatedTags }
          : task
      ));
      
      setParentTasksWithSubtasks(prevTasks => prevTasks.map(task => 
        task.gid === taskId 
          ? { ...task, tags: updatedTags }
          : task
      ));
    }
  }, []);

  const removeTagFromTask = useCallback((taskId: string, tagName: string) => {
    const currentTags = getTaskTags(taskId);
    const updatedTags = currentTags.filter(tag => tag !== tagName);
    setTaskTags(taskId, updatedTags);
    
    // Update the task in state using functional updates to avoid stale closures
    setAssignedTasks(prevTasks => prevTasks.map(task => 
      task.gid === taskId 
        ? { ...task, tags: updatedTags }
        : task
    ));
    
    setParentTasksWithSubtasks(prevTasks => prevTasks.map(task => 
      task.gid === taskId 
        ? { ...task, tags: updatedTags }
        : task
    ));
  }, []);

  return {
    // State
    assignedTasks,
    parentTasksWithSubtasks,
    mentions,
    pendingComments,
    tags,
    loading,
    refreshing,
    commentsLoading,
    commentsSearchCompleted,
    commentsProgress,
    
    // Setters
    setTags,
    setAssignedTasks,
    setParentTasksWithSubtasks,
    
    // Actions
    fetchData,
    fetchCommentsData,
    refreshData,
    addTagToTask,
    removeTagFromTask,
  };
}; 